/**
 * The audio engine: owns the AudioContext, the mixer, and the theme-specific ambience / music /
 * sfx. Everything is created lazily inside unlock() (iOS needs a user gesture) and every public
 * method is safe to call at any time — before unlock, after dispose, or with no Web Audio at all.
 */
import type { MapId } from '@cloudpearl/config';
import { Ambience } from './ambience';
import { Mixer } from './bus';
import { PluckCache } from './instruments/pluck';
import type { Kit, Synth } from './instruments/synth';
import { Music } from './music/music';
import { createImpulse, createNoiseBank } from './noise';
import { createPalette } from './palettes';
import { Sfx } from './sfx';
import { degreeMidi, themeFor, type ThemeDef } from './themes';
import type { AudioEngine, AudioEngineOptions, AudioEvent, AudioFrame, AudioState, AudioVolumes } from './types';
import { Later, Rng, clamp01, finiteOr } from './util';
import { VoicePool } from './voice';
import { createWaves } from './waves';

const THEME_FADE = 1.5;
const SFX_VOICES = 24;
const MUSIC_VOICES = 44;

interface Graph {
  readonly kit: Kit;
  readonly later: Later;
  readonly mixer: Mixer;
  readonly sfxPool: VoicePool;
  readonly musicPool: VoicePool;
  readonly sfxSynth: Synth;
  readonly music: Music;
  readonly sfx: Sfx;
  readonly impulses: Map<MapId, AudioBuffer>;
  amb: Ambience | null;
  theme: ThemeDef | null;
}

type ContextCtor = new (opts?: AudioContextOptions) => AudioContext;

function findContextCtor(): ContextCtor | null {
  const g = globalThis as unknown as { AudioContext?: ContextCtor; webkitAudioContext?: ContextCtor };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

/** True when some AudioContext implementation is reachable (or a factory was supplied). */
export function canCreateAudio(opts?: AudioEngineOptions): boolean {
  return !!opts?.createContext || findContextCtor() !== null;
}

/** Silent fallback (no Web Audio): every call is a cheap no-op. */
export class SilentAudioEngine implements AudioEngine {
  readonly ready = false;
  unlock(): Promise<void> {
    return Promise.resolve();
  }
  setTheme(_id: MapId): void {}
  setVolumes(_v: AudioVolumes): void {}
  setMuted(_muted: boolean): void {}
  update(_frame: AudioFrame, _dt: number): void {}
  play(_event: AudioEvent): void {}
  suspend(): void {}
  resume(): void {}
  dispose(): void {}
}

export class WebAudioEngine implements AudioEngine {
  private ctx: AudioContext | null = null;
  private g: Graph | null = null;
  private disposed = false;
  private userSuspended = false;
  /** resume() requested from a gesture but not settled yet — events may still be scheduled. */
  private resuming = false;
  private warned = false;
  private themeId: MapId;
  private readonly volumes = { master: 0.9, sfx: 0.85, music: 0.45 };
  private muted: boolean;
  private readonly seed: number;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastTick = 0;
  private shapedState: AudioState | null = null;
  private muffleUntil = 0;
  private muffled = false;

  constructor(private readonly opts: AudioEngineOptions = {}) {
    this.themeId = opts.theme === 'dunes' ? 'dunes' : 'sky';
    this.muted = !!opts.muted;
    this.seed = finiteOr(opts.seed, Math.floor(Math.random() * 0xffffffff)) >>> 0;
    if (opts.volumes) this.storeVolumes(opts.volumes);
  }

  get ready(): boolean {
    return !this.disposed && !!this.g && !!this.ctx && this.ctx.state === 'running';
  }

  // ───────── lifecycle ─────────

  unlock(): Promise<void> {
    if (this.disposed) return Promise.resolve();
    try {
      if (!this.ctx) this.ctx = this.createContext();
      const ctx = this.ctx;
      if (!ctx) return Promise.resolve();
      this.userSuspended = false;
      let resumed: Promise<void> | undefined;
      if (ctx.state !== 'running') {
        // Everything here runs synchronously inside the gesture (iOS requirement).
        this.kick(ctx);
        this.resuming = true;
        resumed = ctx.resume();
      }
      if (!this.g) this.build(ctx);
      if (!resumed) return Promise.resolve();
      return resumed.then(
        () => {
          this.resuming = false;
        },
        () => {
          this.resuming = false;
        },
      );
    } catch (err) {
      this.warn(err);
      return Promise.resolve();
    }
  }

  suspend(): void {
    this.userSuspended = true;
    const ctx = this.ctx;
    if (ctx && ctx.state === 'running') ctx.suspend().catch(() => undefined);
  }

  resume(): void {
    this.userSuspended = false;
    const ctx = this.ctx;
    if (ctx && !this.disposed && ctx.state !== 'running' && ctx.state !== 'closed') ctx.resume().catch(() => undefined);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const g = this.g;
    const ctx = this.ctx;
    this.g = null;
    this.ctx = null;
    const teardown = (): void => {
      if (g) {
        try {
          g.music.dispose();
          g.amb?.dispose();
          g.later.flush();
          g.sfxPool.dispose();
          g.musicPool.dispose();
          g.mixer.dispose();
          g.kit.plucks.clear();
        } catch {
          /* ignore */
        }
      }
      if (ctx && ctx.state !== 'closed') ctx.close().catch(() => undefined);
    };
    if (g && ctx && ctx.state === 'running' && typeof setTimeout === 'function') {
      // Fade the master out first so tearing the graph down never clicks.
      try {
        g.mixer.silence(ctx.currentTime);
      } catch {
        /* ignore */
      }
      setTimeout(teardown, 120);
    } else {
      teardown();
    }
  }

  // ───────── settings ─────────

  setTheme(id: MapId): void {
    const next: MapId = id === 'dunes' ? 'dunes' : 'sky';
    if (next === this.themeId && this.g?.theme) return;
    this.themeId = next;
    if (this.g && this.ctx) this.guard(() => this.applyTheme(THEME_FADE));
  }

  setVolumes(v: AudioVolumes): void {
    this.storeVolumes(v);
    this.applyVolumes();
  }

  setMuted(muted: boolean): void {
    this.muted = !!muted;
    const g = this.g;
    if (g && this.ctx) g.mixer.setMuted(this.muted, this.ctx.currentTime);
  }

  // ───────── per frame ─────────

  update(frame: AudioFrame, dt: number): void {
    const g = this.g;
    const ctx = this.ctx;
    if (!g || !ctx || this.disposed || !frame || !this.audible(ctx)) return;
    this.guard(() => {
      const now = ctx.currentTime;
      const step = Math.min(0.1, Math.max(0, finiteOr(dt, 1 / 60)));
      const state = frame.state;
      this.shape(state, now);
      g.amb?.update(frame, step, now);
      g.music.update(state, finiteOr(frame.speedNorm, 0), finiteOr(frame.flow, 0), step);
      this.tick(now);
    });
  }

  play(event: AudioEvent): void {
    const g = this.g;
    const ctx = this.ctx;
    if (!g || !ctx || this.disposed || !event || !this.audible(ctx)) return;
    this.guard(() => {
      const now = ctx.currentTime;
      switch (event.type) {
        case 'start':
          g.music.start(now);
          break;
        case 'crash':
        case 'caught':
          g.music.crash();
          break;
        case 'fall':
          g.music.crash();
          this.muffleUntil = now + 2.8;
          break;
        default:
          break;
      }
      g.sfx.play(event, now);
    });
  }

  // ───────── internals ─────────

  /** Running, or resuming from the gesture that just unlocked us (so a first-tap sound still lands). */
  private audible(ctx: AudioContext): boolean {
    return ctx.state === 'running' || (this.resuming && !this.userSuspended && ctx.state !== 'closed');
  }

  private createContext(): AudioContext | null {
    if (this.opts.createContext) {
      try {
        return this.opts.createContext();
      } catch (err) {
        this.warn(err);
        return null;
      }
    }
    const Ctor = findContextCtor();
    if (!Ctor) return null;
    try {
      return new Ctor({ latencyHint: 'interactive' });
    } catch {
      try {
        return new Ctor();
      } catch (err) {
        this.warn(err);
        return null;
      }
    }
  }

  /** iOS unlock: start a one-sample silent buffer inside the gesture. */
  private kick(ctx: AudioContext): void {
    try {
      const b = ctx.createBuffer(1, 1, ctx.sampleRate);
      const s = ctx.createBufferSource();
      s.buffer = b;
      s.connect(ctx.destination);
      s.start(0);
    } catch {
      /* ignore */
    }
  }

  private build(ctx: AudioContext): void {
    const rng = new Rng(this.seed);
    const kit: Kit = {
      ctx,
      noise: createNoiseBank(ctx, rng),
      waves: createWaves(ctx),
      plucks: new PluckCache(ctx, new Rng(this.seed ^ 0x5bd1e995)),
      rng,
    };
    const later = new Later();
    const mixer = new Mixer(ctx, later, ctx.destination);
    const sfxPool = new VoicePool(ctx, SFX_VOICES);
    const musicPool = new VoicePool(ctx, MUSIC_VOICES);
    const sfxSynth: Synth = { kit, pool: sfxPool, route: mixer.sfx };
    const theme = themeFor(this.themeId);
    this.g = {
      kit,
      later,
      mixer,
      sfxPool,
      musicPool,
      sfxSynth,
      music: new Music({ kit, pool: musicPool, bus: mixer.music, later }),
      sfx: new Sfx(sfxSynth, createPalette(theme, sfxSynth)),
      impulses: new Map(),
      amb: null,
      theme: null,
    };
    this.applyVolumes();
    mixer.setMuted(this.muted, ctx.currentTime);
    this.applyTheme(0.8);
    this.lastTick = ctx.currentTime;
    if (typeof setInterval === 'function') {
      // Fallback clock: keeps music + cleanup alive if the game stops calling update() (menus, stalls).
      this.timer = setInterval(() => this.backgroundTick(), 100);
    }
  }

  private applyTheme(fade: number): void {
    const g = this.g;
    const ctx = this.ctx;
    if (!g || !ctx) return;
    const theme = themeFor(this.themeId);
    if (g.theme === theme) return;
    const now = ctx.currentTime;
    g.theme = theme;
    g.sfx.setPalette(createPalette(theme, g.sfxSynth));

    const old = g.amb;
    if (old) {
      const end = old.fadeOut(now, fade);
      g.later.add(end + 0.1, () => old.dispose());
    }
    g.amb = new Ambience(g.kit, g.sfxPool, g.mixer.amb, theme, now, fade);
    g.music.setTheme(theme, now, fade);

    let ir = g.impulses.get(theme.id);
    if (!ir) {
      ir = createImpulse(ctx, new Rng(this.seed ^ (theme.id === 'dunes' ? 0x2545f491 : 0x9e3779b9)), theme.reverb);
      g.impulses.set(theme.id, ir);
    }
    g.mixer.setReverb(ir, theme.reverbLevel, now, Math.max(0.3, fade));

    if (theme.id === 'dunes') {
      // Pre-render the strings the dunes will need, a note per frame.
      const melody: number[] = [];
      for (let d = 0; d <= 11; d++) melody.push(degreeMidi(theme, d, theme.melodyRoot));
      g.kit.plucks.warm('oud', melody);
      g.kit.plucks.warm('qanun', melody);
      g.kit.plucks.warm('bass', [43, 48, 50, 51]);
      g.kit.plucks.warm('oud', theme.sadMotif);
    }
  }

  /** State shaping: duck / low-pass buses for pause, crash and the post-fall muffle. */
  private shape(state: AudioState, now: number): void {
    const g = this.g;
    if (!g) return;
    const muffle = now < this.muffleUntil;
    if (state === this.shapedState && muffle === this.muffled) return;
    this.shapedState = state;
    this.muffled = muffle;
    const { music, sfx, amb } = g.mixer;
    switch (state) {
      case 'paused':
        music.setDuck(0.5, now, 0.12);
        music.setLowpass(650, now, 0.12);
        sfx.setDuck(0.45, now, 0.12);
        amb.setDuck(0.35, now, 0.12);
        break;
      case 'crashed':
        music.setDuck(0.6, now, 0.45);
        music.setLowpass(900, now, 0.35);
        sfx.setDuck(1, now, 0.2);
        amb.setDuck(0.85, now, 0.5);
        break;
      case 'idle':
        music.setDuck(0.9, now, 0.6);
        music.openLowpass(now, 0.6);
        sfx.setDuck(1, now, 0.2);
        amb.setDuck(1, now, 0.6);
        break;
      default:
        music.setDuck(1, now, 0.4);
        music.openLowpass(now, 0.4);
        sfx.setDuck(1, now, 0.15);
        amb.setDuck(1, now, 0.3);
        break;
    }
    if (muffle) amb.setLowpass(450, now, 0.4);
    else amb.openLowpass(now, 0.8);
  }

  private tick(now: number): void {
    const g = this.g;
    if (!g) return;
    this.lastTick = now;
    g.music.tick(now);
    g.sfxPool.sweep(now);
    g.musicPool.sweep(now);
    g.later.run(now);
    g.kit.plucks.pump(1);
  }

  private backgroundTick(): void {
    const ctx = this.ctx;
    if (!this.g || !ctx || this.disposed || this.userSuspended || ctx.state !== 'running') return;
    const now = ctx.currentTime;
    if (now - this.lastTick > 0.09) this.guard(() => this.tick(now));
  }

  private storeVolumes(v: AudioVolumes): void {
    if (!v) return;
    if (v.master !== undefined) this.volumes.master = clamp01(finiteOr(v.master, this.volumes.master));
    if (v.sfx !== undefined) this.volumes.sfx = clamp01(finiteOr(v.sfx, this.volumes.sfx));
    if (v.music !== undefined) this.volumes.music = clamp01(finiteOr(v.music, this.volumes.music));
  }

  private applyVolumes(): void {
    const g = this.g;
    const ctx = this.ctx;
    if (!g || !ctx) return;
    const now = ctx.currentTime;
    // Perceptual taper: sliders feel linear in loudness.
    const taper = (x: number): number => x * x;
    g.mixer.setMaster(taper(this.volumes.master) * 0.85, now);
    g.mixer.sfx.setVolume(taper(this.volumes.sfx) * 1.2, now);
    g.mixer.music.setVolume(taper(this.volumes.music) * 2.2, now);
  }

  private guard(fn: () => void): void {
    try {
      fn();
    } catch (err) {
      this.warn(err);
    }
  }

  private warn(err: unknown): void {
    if (this.warned) return;
    this.warned = true;
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn('[@cloudpearl/audio] audio error (further errors suppressed):', err);
    }
  }
}
